import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    IconDownload,
    IconId,
    IconPrinter,
    IconRefresh,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import DatePickerField from '@/components/common/DatePickerField';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { recordToolUsage } from '../../../../../services/toolsService';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import {
    deriveToolStatus,
    getToolStatusText,
} from '../../toolPageUtils';
import { usePageZoom } from '../shared/usePagePreviewScale';
import GeoSelectField from '../shared/GeoSelectField';
import { geoCascade } from '../shared/bdGeo';
import MissingFieldsDialog from '../shared/MissingFieldsDialog';
import UnderageNoticeDialog from '../shared/UnderageNoticeDialog';
import { isUnderVoterAge } from '../shared/voterAgeRules';
import SpecialInformationPreviewFrame from './SpecialInformationPreviewFrame';
import PagePreviewZoom from '../shared/PagePreviewZoom';
import SpecialInformationFormInfoBox from './SpecialInformationFormInfoBox';
import { FORM_NO_BOX } from './specialInformationMetrics';
import {
    INITIAL_SPECIAL_INFORMATION_FORM,
    RESIDENCE_TYPES,
    displayField,
    formatDateField,
    getEmptySpecialInformationFields,
    getMobileValidationError,
    getNidValidationError,
    getSpecialInformationFormSnapshot,
    isSpecialInformationFormComplete,
    normalizeBdPhone,
    normalizeDigits,
    parseDateField,
    saveSpecialInformationFormSnapshot,
} from './specialInformationFormEngine';
import { downloadSpecialInformationPdf, printSpecialInformationForm } from './generateSpecialInformationPdf';

const inputBaseClass = 'w-full rounded-xl border bg-white px-3 py-2.5 text-[14px] text-[#3D3833] outline-none transition-colors focus:ring-2 focus:ring-purple-400/30 dark:bg-zinc-900 dark:text-zinc-100';
const inputOkClass = 'border-[#ECE8E4] focus:border-purple-400 dark:border-zinc-700';
const inputErrClass = 'border-red-500 dark:border-red-500';

/** Calendar range: old enough for any applicant's birth date, and no future days. */
const TODAY = new Date();
const BIRTH_FROM_YEAR = 1900;

// The dates on this form that belong to someone cited by their NID — the
// guardian and the two relatives. Their age is worth a check; a passport or
// joining date is not.
const NID_HOLDER_DATE_KEYS = ['guardianBirthDate', 'rel1BirthDate', 'rel2BirthDate', 'rel3BirthDate'];
const RESIDENCE_FROM_YEAR = 1950;

/** Numbers that are checked as they are typed, not only on submit. */
const MOBILE_FIELDS = ['rel1Mobile', 'rel2Mobile', 'rel3Mobile', 'employerMobile'];
const NID_FIELDS = [
    'guardianNid',
    'grandfatherNid',
    'rel1Nid',
    'rel2Nid',
    'rel3Nid',
    'rentOwnerNid',
    'otherOwnerNid',
    'collectorNid',
];

function FormLabel({ children, htmlFor }) {
    return (
        <label htmlFor={htmlFor} className="mb-1 block text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
            {children}
        </label>
    );
}

function FormSection({ number, title, hint, children }) {
    return (
        <div className="rounded-2xl border border-purple-200 bg-white px-4 py-4 dark:border-purple-900/40 dark:bg-zinc-900">
            <h3 className="mb-1 text-[13.5px] font-bold text-purple-700 dark:text-purple-300">
                {number}. {title}
            </h3>
            {hint ? (
                <p className="mb-3 text-[11.5px] leading-relaxed text-[#6B635C] dark:text-zinc-400">{hint}</p>
            ) : <div className="mb-3.5" />}
            {children}
        </div>
    );
}

function FormInput({
    id,
    name,
    value,
    onChange,
    onBlur,
    placeholder,
    error,
    inputMode,
    type = 'text',
    maxLength,
    autoComplete,
    disabled,
}) {
    const hasError = Boolean(error);
    return (
        <div>
            <input
                id={id}
                name={name}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                placeholder={placeholder}
                inputMode={inputMode}
                type={type}
                maxLength={maxLength}
                autoComplete={autoComplete}
                disabled={disabled}
                className={`${inputBaseClass} ${hasError ? inputErrClass : inputOkClass} ${disabled ? 'opacity-50' : ''}`}
            />
            {hasError && typeof error === 'string' && error.trim() ? (
                <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{error}</p>
            ) : null}
        </div>
    );
}

export default function SpecialInformationFormGeneral() {
    const { t } = useTranslation();
    const previewRef = useRef(null);
    const pageZoom = usePageZoom();
    // Which blank the preview should be showing: the one being typed into.
    const [focusField, setFocusField] = useState('');
    const snapshotTimerRef = useRef(null);

    const [form, setForm] = useState(INITIAL_SPECIAL_INFORMATION_FORM);
    const [downloading, setDownloading] = useState(false);
    const [confirmingDownload, setConfirmingDownload] = useState(false);
    // Under-18 birth date on one of the people cited by NID here (the guardian,
    // the two relatives). Warned about, never blocked.
    const [ageNotice, setAgeNotice] = useState(null);
    const [printing, setPrinting] = useState(false);
    const [fieldErrors, setFieldErrors] = useState({});
    const [numberErrors, setNumberErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [hasSavedSnapshot, setHasSavedSnapshot] = useState(false);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setHasSavedSnapshot(!!getSpecialInformationFormSnapshot());
        }
    }, []);

    const update = useCallback((key, value) => {
        setForm((prev) => {
            const next = { ...prev, [key]: value };
            window.clearTimeout(snapshotTimerRef.current);
            if (displayField(next.nameBn) || displayField(next.fatherName)) {
                snapshotTimerRef.current = window.setTimeout(() => {
                    saveSpecialInformationFormSnapshot(next);
                    setHasSavedSnapshot(true);
                }, 400);
            }
            return next;
        });
        setFieldErrors((prev) => {
            if (!prev[key]) return prev;
            if (!displayField(value)) return prev;
            const next = { ...prev };
            delete next[key];
            return next;
        });
    }, []);

    /**
     * Numbers are re-checked as they are typed, but only once the field has
     * been left alone at least once: nobody wants "too short" while they are
     * still halfway through a 17-digit NID.
     */
    const checkNumber = useCallback((key, value) => {
        let message = '';
        if (MOBILE_FIELDS.includes(key)) message = getMobileValidationError(value, t);
        else if (NID_FIELDS.includes(key)) message = getNidValidationError(value, t);
        setNumberErrors((prev) => {
            if (!message && !prev[key]) return prev;
            const next = { ...prev };
            if (message) next[key] = message;
            else delete next[key];
            return next;
        });
        return message;
    }, [t]);

    const handleDigitsChange = (key, raw, maxLength) => {
        const digits = normalizeDigits(raw, maxLength);
        update(key, digits);
        if (touched[key]) checkNumber(key, digits);
    };

    const handlePhoneChange = (key, raw) => {
        const digits = normalizeBdPhone(raw);
        update(key, digits);
        if (touched[key]) checkNumber(key, digits);
    };

    const markTouched = (key) => {
        setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
        checkNumber(key, form[key]);
    };

    /**
     * Remember what was typed, then export — whatever state the form is in.
     *
     * Missing fields deliberately do NOT block the download: a common use of
     * this tool is printing the sheet blank, or half-filled, to complete the
     * rest by hand at the office. Downloading one only asks for a confirmation
     * that names what is still empty, and printing does not even ask.
     */
    const prepareForExport = () => {
        setFieldErrors({});
        saveSpecialInformationFormSnapshot(form);
        setHasSavedSnapshot(true);
    };

    const emptyFields = useMemo(() => getEmptySpecialInformationFields(form, t), [form, t]);

    const runDownloadPdf = async () => {
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('specialInformationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setDownloading(true);
        try {
            recordToolUsage('general/special-information-form', { category: 'general' });
            const base = form.nameBn.trim().replace(/\s+/g, '-').slice(0, 40) || 'application';
            await downloadSpecialInformationPdf(previewRef.current, `special-information-${base}.pdf`);
            notifications.show({
                title: t('common.success'),
                message: t('specialInformationForm.downloadSuccess'),
                color: 'green',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('specialInformationForm.errors.pdfFailed'),
                color: 'red',
            });
        } finally {
            setDownloading(false);
        }
    };

    // The first of those people who is not 18 yet, if any.
    const underageDate = NID_HOLDER_DATE_KEYS
        .map((key) => parseDateField(form[key]))
        .find((date) => isUnderVoterAge(date)) || null;

    const handleDownloadPdf = () => {
        if (underageDate) {
            setAgeNotice({ mode: 'download', date: underageDate });
            return;
        }
        if (emptyFields.length) {
            setConfirmingDownload(true);
            return;
        }
        runDownloadPdf();
    };

    const confirmDownloadPdf = () => {
        setConfirmingDownload(false);
        runDownloadPdf();
    };

    const handlePrint = async () => {
        // Printing is an export too — the same word of warning applies.
        if (underageDate) {
            setAgeNotice({ mode: 'print', date: underageDate });
            return;
        }
        await runPrint();
    };

    const runPrint = async () => {
        recordToolUsage('general/special-information-form', { category: 'general' });
        prepareForExport();
        if (!previewRef.current) {
            notifications.show({
                title: t('common.error'),
                message: t('specialInformationForm.errors.previewNotReady'),
                color: 'red',
            });
            return;
        }

        setPrinting(true);
        try {
            await printSpecialInformationForm(previewRef.current, t('specialInformationForm.errors.popupBlocked'));
            notifications.show({
                title: t('specialInformationForm.printTitle'),
                message: t('specialInformationForm.printSuccess'),
                color: 'blue',
            });
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err?.message || t('specialInformationForm.errors.printFailed'),
                color: 'red',
            });
        } finally {
            setPrinting(false);
        }
    };

    const handleClear = () => {
        setForm(INITIAL_SPECIAL_INFORMATION_FORM);
        setFieldErrors({});
        setNumberErrors({});
        setTouched({});
        notifications.show({
            title: t('specialInformationForm.clearedTitle'),
            message: t('specialInformationForm.clearedMessage'),
            color: 'gray',
        });
    };

    const handleRestoreSnapshot = () => {
        const snapshot = getSpecialInformationFormSnapshot();
        if (!snapshot) {
            notifications.show({
                title: t('specialInformationForm.noSnapshotTitle'),
                message: t('specialInformationForm.noSnapshotMessage'),
                color: 'gray',
            });
            return;
        }

        setForm(snapshot);
        setFieldErrors({});
        setNumberErrors({});
        setTouched({});
        notifications.show({
            title: t('specialInformationForm.restoredTitle'),
            message: t('specialInformationForm.restoredMessage'),
            color: 'green',
        });
    };

    const hasAnyInput = useMemo(
        () => Object.entries(form).some(([key, value]) => (
            typeof INITIAL_SPECIAL_INFORMATION_FORM[key] === 'boolean' ? Boolean(value) : displayField(value)
        )),
        [form],
    );

    const isComplete = useMemo(() => isSpecialInformationFormComplete(form, t), [form, t]);

    const status = deriveToolStatus({
        hasInput: hasAnyInput,
        canRun: isComplete,
        hasResult: isComplete,
    });

    const statusText = getToolStatusText(status, t, {
        ready: t('specialInformationForm.statusReady'),
        success: t('specialInformationForm.statusSuccess'),
    });

    const howToSteps = useMemo(
        () => [1, 2, 3, 4].map((i) => t(`specialInformationForm.howTo.step${i}`)),
        [t],
    );

    const fieldError = (key) => numberErrors[key] || (fieldErrors[key] ? ' ' : undefined);

    /**
     * Division / district / upazila as linked dropdowns (see shared/bdGeo): the
     * first two pick from the list, upazila also takes a typed name.
     */
    const geoField = (key, geo, { selectOnly = false } = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`specialInformationForm.fields.${key}`)}</FormLabel>
            <GeoSelectField
                {...geo}
                selectOnly={selectOnly}
                placeholder={t(`specialInformationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
            />
        </div>
    );

    const fGeo = geoCascade(form, update, { division: 'fDivision', district: 'fDistrict', upazila: 'fUpazila' });
    const gGeo = geoCascade(form, update, { division: 'gDivision', district: 'gDistrict', upazila: 'gUpazila' });

    /** Every plain text field is wired the same way. */
    const textField = (key, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`specialInformationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => update(key, v)}
                placeholder={t(`specialInformationForm.placeholders.${key}`)}
                error={fieldError(key)}
                {...extra}
            />
        </div>
    );

    /**
     * Dates are picked, not typed: every date on this form prints one digit per
     * box as দিন-মাস-বছর, and a picker is the only way that shape comes out
     * right every time. No day after today makes sense on any of them.
     */
    const dateField = (key, { fromYear = RESIDENCE_FROM_YEAR, toYear = TODAY.getFullYear(), future = false } = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`specialInformationForm.fields.${key}`)}</FormLabel>
            <DatePickerField
                value={parseDateField(form[key])}
                onChange={(date) => {
                    update(key, formatDateField(date));
                    // Only the birth dates: a passport or joining date says
                    // nothing about anyone's age.
                    if (NID_HOLDER_DATE_KEYS.includes(key) && isUnderVoterAge(date)) {
                        setAgeNotice({ mode: 'picked', date });
                    }
                }}
                formatValue={formatDateField}
                placeholder={t(`specialInformationForm.placeholders.${key}`)}
                triggerClassName={`${inputBaseClass} ${fieldError(key) ? inputErrClass : inputOkClass}`}
                fromYear={fromYear}
                toYear={toYear}
                disabled={future ? undefined : { after: TODAY }}
                clearable
            />
        </div>
    );

    const digitField = (key, maxLength, extra = {}) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`specialInformationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handleDigitsChange(key, v, maxLength)}
                onBlur={() => markTouched(key)}
                placeholder={t(`specialInformationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="numeric"
                maxLength={maxLength}
                {...extra}
            />
        </div>
    );

    const mobileField = (key) => (
        <div onFocusCapture={() => setFocusField(key)}>
            <FormLabel htmlFor={key}>{t(`specialInformationForm.fields.${key}`)}</FormLabel>
            <FormInput
                id={key}
                name={key}
                value={form[key]}
                onChange={(v) => handlePhoneChange(key, v)}
                onBlur={() => markTouched(key)}
                placeholder={t(`specialInformationForm.placeholders.${key}`)}
                error={fieldError(key)}
                inputMode="tel"
                type="tel"
                maxLength={11}
                autoComplete="tel"
            />
        </div>
    );

    /** One of the three relatives item ৮ asks for, all wired the same way. */
    const relativeBlock = (n) => (
        <div className="space-y-3">
            <p className="text-[12px] font-semibold text-[#3D3833] dark:text-zinc-200">
                {t(`specialInformationForm.sections.relatives.person${n}`)}
            </p>
            {textField(`rel${n}Name`, { autoComplete: 'name' })}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {digitField(`rel${n}Nid`, 17)}
                {textField(`rel${n}Relation`)}
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {dateField(`rel${n}BirthDate`, { fromYear: BIRTH_FROM_YEAR })}
                {mobileField(`rel${n}Mobile`)}
            </div>
        </div>
    );

    return (
        <>
        <UnderageNoticeDialog
            open={Boolean(ageNotice)}
            birthDate={ageNotice?.date ?? null}
            confirmLabel={ageNotice?.mode === 'download'
                ? t('underageNotice.confirmDownload')
                : ageNotice?.mode === 'print' ? t('underageNotice.confirmPrint') : undefined}
            onCancel={() => setAgeNotice(null)}
            onConfirm={() => {
                const mode = ageNotice?.mode;
                setAgeNotice(null);
                if (mode === 'print') { void runPrint(); return; }
                if (mode !== 'download') return;
                if (emptyFields.length) setConfirmingDownload(true);
                else runDownloadPdf();
            }}
        />
        <MissingFieldsDialog
            open={confirmingDownload}
            fields={emptyFields}
            loading={downloading}
            onCancel={() => setConfirmingDownload(false)}
            onConfirm={confirmDownloadPdf}
        />
        <ToolPageLayout
            slug="general/special-information-form"
            subtitleKey="specialInformationForm.subtitle"
            // 30/70: the sheet is what the applicant is checking as they type, and
            // an A4 page needs the room to stay legible, so the fields take the
            // narrow column and the preview becomes the sticky rail beside them.
            // The 9px is half the column gap.
            variant="wideControls"
            railWidthClass="lg:w-[calc(70%_-_9px)]"
            status={status}
            statusText={statusText}
            controlsTitle={t('specialInformationForm.settingsTitle')}
            howToSteps={howToSteps}
            onReset={hasAnyInput ? handleClear : undefined}
            resetLabel={t('specialInformationForm.clearAll')}
            primaryAction={{
                label: t('specialInformationForm.downloadPdf'),
                onClick: handleDownloadPdf,
                disabled: printing,
                loading: downloading,
                icon: IconDownload,
            }}
            // Print and Restore ride in the sticky bar with Download and Clear —
            // eleven sections down, a button parked beside the form is a screen
            // away from wherever you are typing.
            secondaryAction={[
                hasSavedSnapshot ? {
                    key: 'restore',
                    label: t('specialInformationForm.restoreLast'),
                    onClick: handleRestoreSnapshot,
                    disabled: downloading || printing,
                    icon: IconRefresh,
                } : null,
                {
                    key: 'print',
                    label: printing ? t('toolPage.processing') : t('specialInformationForm.print'),
                    onClick: handlePrint,
                    disabled: downloading || printing,
                    icon: IconPrinter,
                },
            ]}
            controls={(
                <div className="space-y-3 font-[Kalpurush,'Noto_Sans_Bengali',sans-serif]">
                    <FormSection
                        number={t('specialInformationForm.sections.applicant.number')}
                        title={t('specialInformationForm.sections.applicant.title')}
                    >
                        <div className="space-y-3">
                            {digitField('formNo', FORM_NO_BOX.maxLength)}
                            {textField('nameBn', { autoComplete: 'name' })}
                            {textField('fatherName', { autoComplete: 'name' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.fatherAddress.number')}
                        title={t('specialInformationForm.sections.fatherAddress.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('fDivision', fGeo.division, { selectOnly: true })}
                                {geoField('fDistrict', fGeo.district, { selectOnly: true })}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('fUpazila', fGeo.upazila)}
                                {digitField('fWardNo', 2)}
                            </div>
                            {textField('fLocalBody')}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('fMouza')}
                                {textField('fVillage', { autoComplete: 'street-address' })}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('fHolding')}
                                {textField('fPostOffice', { autoComplete: 'address-line2' })}
                            </div>
                            {digitField('fPostCode', 4, { autoComplete: 'postal-code' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.guardian.number')}
                        title={t('specialInformationForm.sections.guardian.title')}
                        hint={t('specialInformationForm.sections.guardian.hint')}
                    >
                        <div className="space-y-3">
                            {textField('guardianName', { autoComplete: 'name' })}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {digitField('guardianNid', 17)}
                                {textField('guardianRelation')}
                            </div>
                            {dateField('guardianBirthDate', { fromYear: BIRTH_FROM_YEAR })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.grandfather.number')}
                        title={t('specialInformationForm.sections.grandfather.title')}
                    >
                        <div className="space-y-3">
                            {textField('grandfatherName', { autoComplete: 'name' })}
                            {digitField('grandfatherNid', 17, { disabled: form.grandfatherNidNa })}
                            <label className="flex items-center gap-2 text-[12.5px] text-[#3D3833] dark:text-zinc-200">
                                <input
                                    type="checkbox"
                                    checked={form.grandfatherNidNa}
                                    onChange={(e) => update('grandfatherNidNa', e.target.checked)}
                                    className="h-4 w-4 rounded border-[#ECE8E4] accent-purple-600 dark:border-zinc-700"
                                />
                                {t('specialInformationForm.fields.grandfatherNidNa')}
                            </label>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.grandfatherAddress.number')}
                        title={t('specialInformationForm.sections.grandfatherAddress.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('gDivision', gGeo.division, { selectOnly: true })}
                                {geoField('gDistrict', gGeo.district, { selectOnly: true })}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {geoField('gUpazila', gGeo.upazila)}
                                {digitField('gWardNo', 2)}
                            </div>
                            {textField('gLocalBody')}
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('gMouza')}
                                {textField('gVillage', { autoComplete: 'street-address' })}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('gHolding')}
                                {textField('gPostOffice', { autoComplete: 'address-line2' })}
                            </div>
                            {digitField('gPostCode', 4, { autoComplete: 'postal-code' })}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.relatives.number')}
                        title={t('specialInformationForm.sections.relatives.title')}
                        hint={t('specialInformationForm.sections.relatives.hint')}
                    >
                        <div className="space-y-4">
                            {relativeBlock(1)}
                            {relativeBlock(2)}
                            {relativeBlock(3)}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.residence.number')}
                        title={t('specialInformationForm.sections.residence.title')}
                    >
                        <div className="space-y-3">
                            <div>
                                <FormLabel>{t('specialInformationForm.fields.residenceType')}</FormLabel>
                                <div className="flex flex-wrap gap-2">
                                    {RESIDENCE_TYPES.map((choice) => {
                                        const active = form.residenceType === choice;
                                        return (
                                            <button
                                                key={choice}
                                                type="button"
                                                // Tapping the chosen one again clears it, so the
                                                // sheet can still print with no tenure ticked.
                                                onClick={() => update('residenceType', active ? '' : choice)}
                                                className={`rounded-xl border px-3 py-2 text-[13px] font-semibold transition-colors ${
                                                    active
                                                        ? 'border-purple-400 bg-purple-50 text-purple-700 dark:border-purple-500 dark:bg-purple-950/30 dark:text-purple-300'
                                                        : 'border-[#ECE8E4] bg-white text-[#3D3833] hover:border-purple-300 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-200'
                                                }`}
                                            >
                                                {t(`specialInformationForm.residenceTypes.${choice}`)}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {form.residenceType === 'rent' ? (
                                <div className="space-y-3">
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {dateField('rentFrom')}
                                        {dateField('rentTo', { toYear: TODAY.getFullYear() + 30, future: true })}
                                    </div>
                                    {textField('rentOwnerName', { autoComplete: 'name' })}
                                    {digitField('rentOwnerNid', 17)}
                                </div>
                            ) : null}

                            {form.residenceType === 'other' ? (
                                <div className="space-y-3">
                                    {textField('otherOwnerName', { autoComplete: 'name' })}
                                    {digitField('otherOwnerNid', 17)}
                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                        {dateField('otherFrom')}
                                        {dateField('otherTo', { toYear: TODAY.getFullYear() + 30, future: true })}
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.foreign.number')}
                        title={t('specialInformationForm.sections.foreign.title')}
                        hint={t('specialInformationForm.sections.foreign.hint')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {dateField('arrivalDate')}
                                {dateField('passportIssueDate')}
                            </div>
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {textField('passportIssuePlace')}
                                {dateField('passportExpiryDate', { toYear: TODAY.getFullYear() + 30, future: true })}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.occupation.number')}
                        title={t('specialInformationForm.sections.occupation.title')}
                    >
                        <div className="space-y-3">
                            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                {dateField('jobJoinDate')}
                                {textField('employerName')}
                            </div>
                            {textField('employerAddress', { autoComplete: 'street-address' })}
                            {textField('employerAddress2')}
                            {mobileField('employerMobile')}
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.education.number')}
                        title={t('specialInformationForm.sections.education.title')}
                    >
                        <div className="space-y-4">
                            <div className="space-y-3">
                                {textField('edu1Name')}
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {textField('edu1From')}
                                    {textField('edu1To')}
                                </div>
                                {textField('edu1Result')}
                            </div>
                            <div className="space-y-3">
                                {textField('edu2Name')}
                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                                    {textField('edu2From')}
                                    {textField('edu2To')}
                                </div>
                                {textField('edu2Result')}
                            </div>
                        </div>
                    </FormSection>

                    <FormSection
                        number={t('specialInformationForm.sections.collector.number')}
                        title={t('specialInformationForm.sections.collector.title')}
                        hint={t('specialInformationForm.sections.collector.hint')}
                    >
                        {digitField('collectorNid', 17)}
                    </FormSection>
                </div>
            )}
            preview={(
                // A flex column, not a plain block: the preview panel stretches
                // itself, and inside a block wrapper that height would resolve
                // against nothing.
                <div className="flex flex-col gap-3.5 lg:sticky lg:top-4">
                    <ToolPreviewPanel
                        title={t('specialInformationForm.previewTitle')}
                        status={status}
                        statusLabel={t(`toolPage.status.${status}`)}
                        idleTitle={t('specialInformationForm.previewEmpty')}
                        idleHint={t('specialInformationForm.previewEmptyHint')}
                        showIdle={false}
                        tall
                        flushContent
                        headerAction={<PagePreviewZoom {...pageZoom} />}
                    >
                        {/* Bounded, not min-height: a card taller than the screen
                            cannot stay pinned, so the pages scroll inside it.
                            It takes the whole viewport bar the chrome above and
                            the action bar below, so the rail is not left half
                            empty beside a form eleven sections long. */}
                        <div className="flex h-[min(1100px,calc(100vh-186px))] min-h-[420px] flex-col">
                            <div className="mb-2 flex items-center gap-1.5 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                <IconId size={14} stroke={1.8} />
                                {t('specialInformationForm.livePreview')}
                            </div>
                            <SpecialInformationPreviewFrame
                                form={form}
                                previewRef={previewRef}
                                zoom={pageZoom.zoom}
                                focusField={focusField}
                            />
                        </div>
                    </ToolPreviewPanel>
                </div>
            )}
            belowWorkArea={<SpecialInformationFormInfoBox />}
        />
        </>
    );
}
